import React, { useState, useEffect } from "react";
import { Dialog } from "@themes/pollinator/ui/Dialog";
import { Type } from "@themes/pollinator/ui/Type";
import { useDialog } from "@platform/store";

interface DeviceInfo {
  isMobile: boolean;
  browser: "chrome" | "firefox" | "safari" | "edge" | "other";
  os: "ios" | "android" | "windows" | "macos" | "linux" | "other";
}

const VideoMeetingPermissionsGuide: React.FC = () => {
  const { activeDialog, closeDialog } = useDialog();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo | null>(null);
  const [, setPermissionState] = useState<
    "unknown" | "granted" | "denied" | "prompt"
  >("unknown");

  useEffect(() => {
    // Detect device and browser info
    const detectDevice = (): DeviceInfo => {
      const userAgent = navigator.userAgent.toLowerCase();

      const isMobile =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
          userAgent
        );

      let browser: DeviceInfo["browser"] = "other";
      // Check for iOS Chrome first (CriOS is the key identifier)
      if (userAgent.includes("crios")) {
        browser = "chrome";
      }
      // Check for regular Chrome (but not Edge)
      else if (userAgent.includes("chrome") && !userAgent.includes("edg")) {
        browser = "chrome";
      }
      // Check for Firefox
      else if (userAgent.includes("firefox")) {
        browser = "firefox";
      }
      // Check for Safari (but not Chrome-based browsers)
      else if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
        browser = "safari";
      }
      // Check for Edge
      else if (userAgent.includes("edg")) {
        browser = "edge";
      }

      let os: DeviceInfo["os"] = "other";
      if (userAgent.includes("iphone") || userAgent.includes("ipad")) {
        os = "ios";
      } else if (userAgent.includes("android")) {
        os = "android";
      } else if (userAgent.includes("windows")) {
        os = "windows";
      } else if (userAgent.includes("mac")) {
        os = "macos";
      } else if (userAgent.includes("linux")) {
        os = "linux";
      }

      return { isMobile, browser, os };
    };

    setDeviceInfo(detectDevice());

    // Check current permission state
    const checkPermissions = async () => {
      try {
        const result = await navigator.permissions.query({
          name: "camera" as PermissionName,
        });
        setPermissionState(result.state);
      } catch (error) {
        setPermissionState("unknown");
      }
    };

    checkPermissions();
  }, []);

  const getInstructions = () => {
    if (!deviceInfo) return null;

    const { isMobile, browser, os } = deviceInfo;

    if (isMobile) {
      if (os === "ios") {
        // Check if it's iOS Chrome specifically
        if (browser === "chrome") {
          return {
            title: "Reset Camera & Microphone Permissions on iOS Chrome",
            steps: [
              "Open Settings on your iPhone or iPad",
              "Scroll down and tap 'Apps'",
              'Scroll down and tap "Chrome"',
              'Toggle "Camera" permission on',
              'Toggle "Microphone" permission on',
              "Return to this page and refresh",
              "Note: iOS Chrome has limited camera/microphone support. Consider using Safari for better compatibility.",
            ],
          };
        } else {
          return {
            title: "Reset Camera & Microphone Permissions on iOS Safari",
            steps: [
              "Open Settings on your iPhone or iPad",
              "Scroll down and tap 'Apps'",
              'Scroll down and tap "Safari"',
              "Scroll down and tap 'Camera'",
              'Set to "Ask" or "Allow"',
              "Go back to the previous screen",
              "Scroll down and tap 'Microphone'",
              'Set to "Ask" or "Allow"',
              "Return to this page and refresh",
            ],
          };
        }
      } else if (os === "android") {
        return {
          title: "Reset Camera & Microphone Permissions on Android",
          steps: [
            "Tap the settings icon in the address bar",
            'Select "Allow" for both camera and microphone',
            "If you don't see the icon, open Settings on your device",
            'Tap "Apps" or "Application Manager"',
            "Find and tap your browser (Chrome, Firefox, etc.)",
            'Tap "Permissions"',
            'Enable "Camera" and "Microphone" permissions',
            "Return to this page and refresh",
          ],
        };
      }
    } else {
      // Desktop instructions
      if (browser === "chrome") {
        return {
          title: "Reset Camera & Microphone Permissions in Chrome",
          steps: [
            "Click the icon in the address bar.",
            'Toggle "Camera" permission on',
            'Toggle "Microphone" permission on',
            "Refresh this page",
            "If still blocked, go to Chrome Settings > Privacy and Security > Site Settings > Camera/Microphone",
          ],
        };
      } else if (browser === "firefox") {
        return {
          title: "Reset Camera & Microphone Permissions in Firefox",
          steps: [
            "Click the settings icon in the address bar.",
            "Remove blocked permissions for camera and microphone.",
            "Refresh this page",
            "If still blocked, go to Firefox Settings > Privacy & Security > Permissions > Camera/Microphone",
          ],
        };
      } else if (browser === "safari") {
        return {
          title: "Reset Camera & Microphone Permissions in Safari",
          steps: [
            "Go to Safari menu > Settings (or Preferences)",
            'Click the "Websites" tab',
            'Select "Camera" and set to "Allow"',
            'Select "Microphone" and set to "Allow"',
            "Refresh this page",
          ],
        };
      } else if (browser === "edge") {
        return {
          title: "Reset Camera & Microphone Permissions in Edge",
          steps: [
            "Click the icon in the address bar (next to the URL)",
            'Click "Camera" and select "Allow"',
            'Click "Microphone" and select "Allow"',
            "Refresh this page",
            "If still blocked, go to Edge Settings > Site permissions > Camera/Microphone",
          ],
        };
      } else {
        return {
          title: "Reset Camera & Microphone Permissions",
          steps: [
            "Look for an icon or camera/microphone icon in your browser's address bar",
            'Click it and select "Allow" for both camera and microphone',
            "Refresh this page",
            "If you don't see any icon, check your browser's privacy settings",
          ],
        };
      }
    }

    return {
      title: "Reset Camera & Microphone Permissions",
      steps: [
        "Check your browser settings for camera and microphone permissions",
        "Make sure this site is allowed to access your camera and microphone",
        "Refresh this page and try again",
      ],
    };
  };

  const instructions = getInstructions();
  const isOpen = activeDialog === "permissionsGuide";

  return (
    <>
      {/* Instructions Dialog */}
      <Dialog
        isOpen={isOpen}
        onClose={() => closeDialog()}
        title={instructions?.title || "Permission Instructions"}
        description="Follow these steps to enable camera and microphone access:"
        isWithActions={true}
        primaryButtonText="I've Updated Permissions"
        onPrimaryClick={() => {
          closeDialog();
          // Refresh the page to retry permissions
          window.location.reload();
        }}
        secondaryButtonText="Cancel"
        onSecondaryClick={() => closeDialog()}
        size="xl"
      >
        {instructions && (
          <div className="flex flex-col gap-8">
            <ol className="list-decimal list-inside flex flex-col gap-2">
              {instructions.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>

            <Type variant="body2">
              After updating permissions, refresh this page to try again.
            </Type>
          </div>
        )}
      </Dialog>
    </>
  );
};

export default VideoMeetingPermissionsGuide;
